import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import { Package, ArrowRight, AlertOctagon, RotateCcw, Truck, CheckCircle2, Factory, Edit3, X, Save, Search, MessageSquare } from "lucide-react";
import api, { formatError } from "../lib/api";

// HO Approval removed (06/2026 client decision) — rewards dispatch within 7 days at end-of-program
const STAGES = [
  { id: "selected", label: "Selected", icon: Package, tone: "slate" },
  { id: "vendor_dispatch", label: "Vendor Dispatch", icon: Factory, tone: "teal" },
  { id: "in_transit", label: "In Transit", icon: Truck, tone: "teal" },
  { id: "delivered", label: "Delivered", icon: CheckCircle2, tone: "emerald" },
  { id: "exception", label: "Exception", icon: AlertOctagon, tone: "rose" },
  { id: "rto", label: "RTO", icon: RotateCcw, tone: "rose" },
];

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—";

export default function Fulfillment() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [remarkDrafts, setRemarkDrafts] = useState({});
  // Filters
  const [q, setQ] = useState("");
  const [dseFilter, setDseFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");

  async function fetchQueue() {
    const { data } = await api.get("/fulfillment/queue");
    setItems(data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/fulfillment/queue");
        if (alive) setItems(data);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  const dseOptions = useMemo(() => [...new Set(items.map((r) => r.dse_name).filter(Boolean))].sort(), [items]);
  const stateOptions = useMemo(() => [...new Set(items.map((r) => r.physio_state).filter(Boolean))].sort(), [items]);
  const regionOptions = useMemo(() => [...new Set(items.map((r) => r.physio_region).filter(Boolean))].sort(), [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((r) => {
      if (dseFilter && r.dse_name !== dseFilter) return false;
      if (stateFilter && r.physio_state !== stateFilter) return false;
      if (regionFilter && r.physio_region !== regionFilter) return false;
      if (needle) {
        const hay = [r.physio_name, r.erp_outlet_id, r.physio_mobile, r.reward_name, r.physio_city]
          .map((x) => String(x ?? "").toLowerCase()).join(" ");
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, q, dseFilter, stateFilter, regionFilter]);

  const filtersActive = q || dseFilter || stateFilter || regionFilter;
  function clearFilters() { setQ(""); setDseFilter(""); setStateFilter(""); setRegionFilter(""); }

  async function advance(id, stage, extras = {}) {
    try { await api.patch(`/fulfillment/${id}/stage`, { stage, ...extras }); await fetchQueue(); }
    catch (e) { alert(formatError(e)); }
  }

  async function saveRemark(r) {
    const remark = remarkDrafts[r.id] ?? r.remark ?? "";
    try {
      await api.patch(`/fulfillment/${r.id}/stage`, { stage: r.stage, remark });
      setRemarkDrafts((d) => { const n = { ...d }; delete n[r.id]; return n; });
      await fetchQueue();
    } catch (e) { alert(formatError(e)); }
  }

  function startEdit(r) {
    setEditingId(r.id);
    setEditForm({
      vendor: r.vendor || "",
      vendor_contact: r.vendor_contact || "",
      tracking_id: r.tracking_id || "",
      dispatch_date: r.dispatch_date ? r.dispatch_date.slice(0, 10) : "",
      expected_delivery_date: r.expected_delivery_date ? r.expected_delivery_date.slice(0, 10) : "",
    });
  }
  async function saveEdit(r) {
    try {
      await api.patch(`/fulfillment/${r.id}/stage`, { stage: r.stage, ...editForm });
      setEditingId(null);
      await fetchQueue();
    } catch (e) { alert(formatError(e)); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Operations"
        title="Reward Fulfillment Lifecycle"
        subtitle="Selected → Vendor Dispatch → In Transit → Delivered, with Exception and RTO paths. No HO Approval gate — rewards dispatch within 7 days at the end of the program. Each card carries physio · ERP ID · mobile · DSE · address · vendor · tracking ID · dispatch & expected delivery dates."
        testId="fulfillment-page"
      />

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-3" data-testid="fulfillment-filter-bar">
        <div className="flex-1 min-w-[200px]">
          <label className="small-caps text-slate-500 text-[11px]">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Physio, ERP ID, mobile, reward…" className="pl-8 !py-1.5 !text-[12px] w-full" data-testid="fulfillment-search" />
          </div>
        </div>
        <div className="min-w-[150px]">
          <label className="small-caps text-slate-500 text-[11px]">DSE</label>
          <select value={dseFilter} onChange={(e) => setDseFilter(e.target.value)} className="w-full !py-1.5 !text-[12px]" data-testid="fulfillment-filter-dse">
            <option value="">All DSEs</option>
            {dseOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="min-w-[140px]">
          <label className="small-caps text-slate-500 text-[11px]">Region</label>
          <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} className="w-full !py-1.5 !text-[12px]" data-testid="fulfillment-filter-region">
            <option value="">All regions</option>
            {regionOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="min-w-[140px]">
          <label className="small-caps text-slate-500 text-[11px]">State</label>
          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="w-full !py-1.5 !text-[12px]" data-testid="fulfillment-filter-state">
            <option value="">All states</option>
            {stateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">{filtered.length} of {items.length}</span>
          {filtersActive && <button onClick={clearFilters} className="btn-secondary !py-1.5 !text-[12px]" data-testid="fulfillment-clear-filters">Clear</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="fulfillment-board">
        {STAGES.map((s) => {
          const list = filtered.filter((r) => r.stage === s.id);
          return (
            <div key={s.id} className="bg-white border border-slate-200 rounded-lg p-3 min-h-[260px]" data-testid={`fulfillment-col-${s.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium text-[13px] text-slate-900 flex items-center gap-1.5"><s.icon size={14} /> {s.label}</div>
                <Tag tone={s.tone}>{list.length}</Tag>
              </div>
              <div className="space-y-2">
                {list.slice(0, 25).map((r) => (
                  <div key={r.id} className="surface p-3 text-[12px] space-y-1.5" data-testid={`fulfillment-card-${r.id}`}>
                    {/* Reward + points */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-slate-900 flex-1 leading-tight">{r.reward_name}</div>
                      <button onClick={() => editingId === r.id ? setEditingId(null) : startEdit(r)} className="text-slate-400 hover:text-teal-700 shrink-0" data-testid={`fulfillment-edit-${r.id}`}>
                        {editingId === r.id ? <X size={12} /> : <Edit3 size={12} />}
                      </button>
                    </div>
                    <div className="text-[11px] text-slate-500">{fmt(r.points)} pts</div>

                    {editingId === r.id ? (
                      <div className="space-y-1.5 pt-1 border-t border-slate-100">
                        <input value={editForm.vendor} onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })} placeholder="Vendor" className="!py-1 !text-[11px]" data-testid={`fulfillment-vendor-${r.id}`} />
                        <input value={editForm.vendor_contact} onChange={(e) => setEditForm({ ...editForm, vendor_contact: e.target.value })} placeholder="Vendor contact" className="!py-1 !text-[11px]" />
                        <input value={editForm.tracking_id} onChange={(e) => setEditForm({ ...editForm, tracking_id: e.target.value })} placeholder="Tracking ID" className="!py-1 !text-[11px]" data-testid={`fulfillment-tracking-${r.id}`} />
                        <div className="grid grid-cols-2 gap-1.5">
                          <input type="date" value={editForm.dispatch_date} onChange={(e) => setEditForm({ ...editForm, dispatch_date: e.target.value })} className="!py-1 !text-[10.5px]" />
                          <input type="date" value={editForm.expected_delivery_date} onChange={(e) => setEditForm({ ...editForm, expected_delivery_date: e.target.value })} className="!py-1 !text-[10.5px]" />
                        </div>
                        <button onClick={() => saveEdit(r)} className="btn-teal !py-1 !text-[10.5px] w-full inline-flex items-center justify-center gap-1" data-testid={`fulfillment-save-${r.id}`}><Save size={10} /> Save</button>
                      </div>
                    ) : (
                      <>
                        {/* Physio block */}
                        <div className="pt-1 border-t border-slate-100">
                          <div className="font-medium text-slate-800 text-[12px]">{r.physio_name}</div>
                          <div className="text-[10.5px] text-slate-500 font-mono">{r.erp_outlet_id || "—"} · {r.physio_mobile || "—"}</div>
                          {(r.physio_city || r.physio_state) && (
                            <div className="text-[10.5px] text-slate-500">{r.physio_city}{r.physio_city && r.physio_state ? ", " : ""}{r.physio_state}</div>
                          )}
                          {r.physio_address && <div className="text-[10.5px] text-slate-500 line-clamp-2">{r.physio_address}</div>}
                        </div>
                        {/* DSE */}
                        {r.dse_name && <div className="text-[10.5px] text-slate-500"><span className="small-caps">DSE:</span> {r.dse_name}{r.dse_erp_id ? ` · ${r.dse_erp_id}` : ""}</div>}
                        {/* Vendor + tracking */}
                        {(r.vendor || r.tracking_id || r.dispatch_date || r.expected_delivery_date) && (
                          <div className="pt-1 border-t border-slate-100 space-y-0.5">
                            {r.vendor && <div className="text-[10.5px]"><span className="small-caps text-slate-400">Vendor:</span> {r.vendor}</div>}
                            {r.tracking_id && <div className="text-[10.5px]"><span className="small-caps text-slate-400">Track:</span> <span className="font-mono">{r.tracking_id}</span></div>}
                            {(r.dispatch_date || r.expected_delivery_date) && (
                              <div className="text-[10.5px] flex items-center gap-2">
                                <span><span className="small-caps text-slate-400">Disp:</span> {fmtDate(r.dispatch_date)}</span>
                                <span><span className="small-caps text-slate-400">ETA:</span> {fmtDate(r.expected_delivery_date)}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Remark per ticket */}
                        <div className="pt-1 border-t border-slate-100">
                          <div className="flex items-center gap-1">
                            <MessageSquare size={11} className="text-slate-400 shrink-0" />
                            <input
                              value={remarkDrafts[r.id] ?? r.remark ?? ""}
                              onChange={(e) => setRemarkDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                              placeholder="Add remark…"
                              className="!py-1 !text-[11px] flex-1"
                              data-testid={`fulfillment-remark-input-${r.id}`}
                            />
                            {(remarkDrafts[r.id] !== undefined && remarkDrafts[r.id] !== (r.remark ?? "")) && (
                              <button onClick={() => saveRemark(r)} className="btn-teal !py-1 !px-2 !text-[10px]" data-testid={`fulfillment-remark-save-${r.id}`}>Save</button>
                            )}
                          </div>
                        </div>
                        {/* Stage advancement */}
                        <div className="flex flex-wrap items-center gap-1 pt-1">
                          {STAGES.filter((x) => x.id !== s.id).map((x) => (
                            <button key={x.id} onClick={() => advance(r.id, x.id)} className="text-[9.5px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-100 text-slate-600 flex items-center gap-0.5" data-testid={`fulfillment-advance-${r.id}-${x.id}`}>
                              <ArrowRight size={8} /> {x.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {list.length === 0 && <div className="text-[11px] text-slate-400 text-center py-4">Empty</div>}
                {list.length > 25 && <div className="text-[11px] text-slate-500 text-center pt-1">+ {list.length - 25} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
